<template>
  <!-- Solo l'elenco delle condizioni e dei servizi, senza la card (pagina di acquisto) -->
  <ul v-if="soloElenco" class="plan-sheet__list">
    <li v-for="(riga, i) in righe" :key="i">
      <component :is="pezzo.forte ? 'strong' : 'span'" v-for="(pezzo, j) in riga" :key="j">{{ pezzo.testo }}</component>
    </li>
  </ul>
  <!-- La card del piano (pattern .plan-sheet di _pages.scss): nome, prezzo e
       righe arrivano dai dati del piano, la forma dal sito -->
  <article
    v-else
    class="plan-sheet"
    :class="{ 'plan-sheet--current': corrente }"
    :aria-labelledby="idNome"
  >
    <!-- Senza etichetta resta il suo posto (vuoto): nella griglia i nomi delle
         card stanno sulla stessa riga -->
    <p v-if="etichetta" class="plan-sheet__tag">{{ etichetta }}</p>
    <p v-else class="plan-sheet__tag plan-sheet__tag--empty" aria-hidden="true">&nbsp;</p>
    <h3 :id="idNome" class="plan-sheet__name">{{ piano.nome }}</h3>
    <p class="plan-sheet__price">
      <span class="plan-sheet__amount">{{ prezzo }}</span>
      <span v-if="periodo" class="plan-sheet__period">{{ periodo }}</span>
    </p>
    <div v-if="formule.length > 1" class="themed-button-group plan-sheet__billing">
      <b-form-radio-group
        v-model="slugScelto"
        :options="formule"
        buttons
        button-variant="outline-secondary"
        size="sm"
        :aria-label="$t('PlanSheet.formula')"
      ></b-form-radio-group>
    </div>
    <p v-if="piano.descrizione" class="plan-sheet__lead">{{ piano.descrizione }}</p>
    <ul
      v-if="righe.length"
      :id="idElenco"
      class="plan-sheet__list"
      :class="{ 'plan-sheet__list--short': righeNascoste > 0 && !elencoAperto }"
    >
      <li v-for="(riga, i) in righe" :key="i">
        <component :is="pezzo.forte ? 'strong' : 'span'" v-for="(pezzo, j) in riga" :key="j">{{ pezzo.testo }}</component>
      </li>
    </ul>
    <!-- Sul telefono le card scorrono di lato e il nastro è alto quanto la più
         lunga: gli elenchi lunghi si fermano alle prime righe -->
    <button
      v-if="righeNascoste > 0"
      type="button"
      class="back-link plan-sheet__more d-md-none"
      :aria-expanded="elencoAperto ? 'true' : 'false'"
      :aria-controls="idElenco"
      @click="elencoAperto = !elencoAperto"
    >
      {{ elencoAperto ? $t("PlanSheet.mostraMeno") : $t("PlanSheet.mostraTutti", { n: righeNascoste }) }}
      <b-icon :icon="elencoAperto ? 'chevron-up' : 'chevron-down'" aria-hidden="true"></b-icon>
    </button>
    <div v-if="dettaglio.note.length || $scopedSlots.azioni" class="plan-sheet__foot">
      <p v-for="(nota, i) in dettaglio.note" :key="i" class="plan-sheet__note">
        <component :is="pezzo.forte ? 'strong' : 'span'" v-for="(pezzo, j) in nota" :key="j">{{ pezzo.testo }}</component>
      </p>
      <div v-if="$scopedSlots.azioni" class="plan-sheet__actions">
        <slot name="azioni" :piano="piano"></slot>
      </div>
    </div>
  </article>
</template>

<script>
import {
  leggiDettaglio,
  righeCondizioni,
  formattaPrezzo,
  periodoDelPiano,
  formulaDelPiano,
} from "@/_utils/pianiSottoscrizione";

// Righe dell'elenco visibili sul telefono prima di «Mostra tutti»: quelle dei
// piani EASY ci stanno intere (tenere uguale a :nth-child in _pages.scss §6bis)
const RIGHE_SUL_TELEFONO = 6;

export default {
  name: "PlanSheet",
  props: {
    // Le formule dello stesso piano (raggruppaPiani): una card sola, con la
    // scelta mensile o annuale quando sono più di una
    piani: {
      type: Array,
      required: true,
    },
    // Il piano della persona: la card prende l'anello e l'etichetta «Il tuo piano»
    slugCorrente: {
      type: String,
      default: null,
    },
    // La formula mostrata all'apertura, per esempio quella in attesa di pagamento
    slugIniziale: {
      type: String,
      default: null,
    },
    // Anche le condizioni a prezzo generale: la card del piano gratuito
    tutteLeCondizioni: {
      type: Boolean,
      default: false,
    },
    // Solo l'elenco, senza la card
    soloElenco: {
      type: Boolean,
      default: false,
    },
    // Sul telefono l'elenco lungo si ferma alle prime righe (non in una card da sola)
    compattabile: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      slugScelto: this.formulaDiPartenza(),
      elencoAperto: false,
    };
  },
  computed: {
    piano() {
      return this.piani.find((p) => p.slug === this.slugScelto) || this.piani[0] || {};
    },
    corrente() {
      return !!this.slugCorrente && this.piani.some((p) => p.slug === this.slugCorrente);
    },
    dettaglio() {
      return leggiDettaglio(this.piano.dettaglio);
    },
    etichetta() {
      return this.corrente ? this.$t("PlanSheet.ilTuoPiano") : this.dettaglio.etichetta;
    },
    prezzo() {
      return Number(this.piano.prezzo) > 0
        ? formattaPrezzo(this.piano.prezzo, this.$i18n.locale, { spazio: false })
        : this.$t("PlanSheet.gratis");
    },
    periodo() {
      return Number(this.piano.prezzo) > 0 ? periodoDelPiano(this.piano, this.traduci) : "";
    },
    formule() {
      return this.piani.map((p) => ({ value: p.slug, text: formulaDelPiano(p, this.traduci) }));
    },
    // Prima il colloquio iniziale (servizi in testa), poi le condizioni calcolate
    // (chiave in grassetto), poi gli altri servizi del dettaglio
    righe() {
      const condizioni = righeCondizioni(this.piano.condizioni, this.traduci, this.$i18n.locale, {
        tutte: this.tutteLeCondizioni,
      }).map((riga) => [
        { testo: riga.chiave + ":", forte: true },
        { testo: " " + riga.valore + (riga.asterisco ? "*" : ""), forte: false },
      ]);
      return this.dettaglio.inTesta.concat(condizioni, this.dettaglio.servizi);
    },
    idNome() {
      return "piano-" + this._uid;
    },
    idElenco() {
      return "piano-" + this._uid + "-servizi";
    },
    // Righe che sul telefono restano nascoste finché non si apre l'elenco
    righeNascoste() {
      return this.compattabile ? Math.max(this.righe.length - RIGHE_SUL_TELEFONO, 0) : 0;
    },
  },
  watch: {
    piani() {
      if (!this.piani.some((p) => p.slug === this.slugScelto)) {
        this.slugScelto = this.formulaDiPartenza();
      }
    },
  },
  methods: {
    traduci(chiave, valori) {
      return this.$t(chiave, valori);
    },
    formulaDiPartenza() {
      const preferita = [this.slugCorrente, this.slugIniziale].find(
        (slug) => slug && this.piani.some((p) => p.slug === slug)
      );
      return preferita || (this.piani[0] ? this.piani[0].slug : null);
    },
  },
};
</script>
